import Icon from '@nxsys/components/AppIconImage';
import {
  IconDashboard,
  IconAnalytics,
  IconCompanies,
  IconEmployee,
  IconAgency,
  IconProspects,
  IconPaymentSettings,
  IconTimesheets,
  IconInvoices,
  IconActivityLogs,
  IconPermissions,
} from '@nxsys/assets/icons';
import {
  TbClipboardList,
  TbCloudDownload,
  TbDatabaseImport,
  TbCoins,
  TbReceiptRefund,
  TbReportAnalytics,
  TbFileText,
  TbFileImport,
  TbFileExport,
  TbId,
  TbHistory,
  TbBell,
  TbSend,
} from 'react-icons/tb';
import { PayrollCountryType } from '@nxsys/types/models/Common';
import { roles } from '@nxsys/store/constants';
import { composeUmbrellaIrelandRoutes } from './umbrellaIrelandRoutes';

const { admin, employee, employer, agency } = roles;

/**
 * Ireland-only sidebar items, module-scoped so `bureauIrelandRoutesConfig`
 * (below) and the umbrella×Ireland composition (bottom of this file) render
 * the identical object — extracted, not duplicated.
 */
const rpnMenuItem = {
  id: 'rpn',
  title: 'RPN Updates',
  messageId: 'sidebar.app.rpnUpdates',
  icon: <TbCloudDownload />,
  url: '/rpn',
  allowedRoles: [admin, employer],
  permissionNames: ['COMPANY.RPN_UPDATES.LIST'],
};
const aepnMenuItem = {
  id: 'aepn',
  title: 'AEPN Updates',
  messageId: 'sidebar.app.aepnUpdates',
  icon: <TbBell />,
  url: '/aepn',
  allowedRoles: [admin, employer],
  permissionNames: ['COMPANY.AEPN_UPDATES.LIST'],
};
const submitPsrMenuItem = {
  id: 'submit-psr',
  title: 'Submit PSR',
  messageId: 'sidebar.app.submitPSR',
  icon: <TbSend />,
  url: '/submit-psr',
  allowedRoles: [admin, employer],
  permissionNames: ['COMPANY.SUBMIT_PSR.LIST'],
};
const revenueFilingsHistoryMenuItem = {
  id: 'revenue-filings-history',
  title: 'Revenue Filings History',
  messageId: 'sidebar.app.RevenueFilingsHistory',
  type: 'collapse',
  icon: <TbHistory />,
  allowedRoles: [admin, employer],
  permissionNames: ['COMPANY.RTI_FILINGS.LIST'],
  children: [
    {
      id: 'revenue-filings/psr',
      title: 'PSR',
      messageId: 'sidebar.app.RevenueFilingsHistory.PSR',
      url: '/revenue-filings/psr',
      allowedRoles: [admin, employer],
      permissionNames: ['COMPANY.RTI_FILINGS.LIST'],
    },
  ],
};

const bureauRoutesConfig = [
  {
    id: 'dashboards',
    title: 'Dashboard',
    messageId: 'sidebar.app.dashboard',
    icon: <Icon src={IconDashboard} alt="Dashboard" />,
    url: '/dashboards',
    allowedRoles: [admin, employer, employee],
    permissionNames: ['COMPANY.DASHBOARD.LIST'],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'analytics',
    title: 'Analytics',
    messageId: 'sidebar.app.analytics',
    icon: <Icon src={IconAnalytics} alt="Analytics" />,
    url: '/analytics',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.ANALYTICS.LIST'],
  },
  {
    id: 'companies',
    title: 'Employers',
    messageId: 'common.bureau.companies',
    icon: <Icon src={IconCompanies} alt="Companies" />,
    url: '/companies',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.COMPANIES.LIST'],
  },
  {
    id: 'general',
    title: 'Profile',
    messageId: 'sidebar.app.profile',
    icon: <Icon src={IconEmployee} alt="Profile" />,
    url: '/general',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'payslip',
    title: 'Payslip',
    messageId: 'sidebar.app.payslips',
    icon: <Icon src={IconInvoices} alt="Payslip" />,
    url: '/payslip',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'earning-statement',
    title: 'CIS Statement of Earnings',
    messageId: 'sidebar.app.cisStatementOfEarnings',
    icon: <Icon src={IconInvoices} alt="CIS Statement" />,
    url: '/earning-statement',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'payroll',
    title: 'Payroll',
    messageId: 'sidebar.app.payroll',
    icon: <Icon src={IconInvoices} alt="Payroll" />,
    url: '/payroll',
    allowedRoles: [employer],
    permissionNames: ['COMPANY.TIMESHEETS.LIST'],
  },
  { type: 'divider' } as any,
  {
    id: 'employees-subcontractors',
    title: 'Employees',
    messageId: 'sidebar.app.employeeSubcontractors',
    icon: <Icon src={IconEmployee} alt="Employees" />,
    url: '/employees-subcontractors',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.EMPLOYEES.LIST'],
  },
  {
    id: 'prospects',
    title: 'Prospects',
    messageId: 'sidebar.app.prospects',
    icon: <Icon src={IconProspects} alt="Prospects" />,
    url: '/prospects',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.PROSPECTS.LIST'],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'hmrc-update',
    title: 'HMRC Update',
    messageId: 'sidebar.app.hmrcUpdate',
    type: 'collapse',
    icon: <TbCloudDownload />,
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.HMRC_UPDATES.LIST'],
    children: [
      {
        id: 'hmrc-update/p6-p9',
        title: 'P6/P9 Update',
        messageId: 'sidebar.app.hmrcUpdate.p6p9',
        url: 'hmrc-update/p6-p9',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.HMRC_UPDATES.LIST'],
      },
      {
        id: 'hmrc-update/student-loan',
        title: 'P6/P9 Update',
        messageId: 'sidebar.app.hmrcUpdate.studentLoan',
        url: 'hmrc-update/student-loan',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.HMRC_UPDATES.LIST'],
      },
    ],
  },
  {
    id: 'bulk-update',
    title: 'Bulk Update',
    messageId: 'sidebar.app.bulkUpdate',
    icon: <TbDatabaseImport />,
    url: '/bulk-update',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.BULK_UPDATE.LIST'],
  },
  {
    id: 'statutory-payments',
    title: 'Statutory Payments',
    messageId: 'sidebar.app.statutory',
    type: 'collapse',
    icon: <TbCoins />,
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.STATUTORY_PAYMENTS.LIST'],
    children: [
      {
        id: 'statutory-leave',
        title: 'Leave',
        messageId: 'sidebar.app.statutory.leave',
        url: '/statutory-leave',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.STATUTORY_PAYMENTS.LIST'],
      },
      {
        id: 'statutory-payment',
        title: 'Payments',
        messageId: 'sidebar.app.statutory.payments',
        url: '/statutory-payment',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.STATUTORY_PAYMENTS.LIST'],
      },
    ],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'payments-settings',
    title: 'Payments Settings',
    messageId: 'sidebar.app.paymentsSettings',
    type: 'collapse',
    icon: <Icon src={IconPaymentSettings} alt="Payments Settings" />,
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.PAYMENT_SETTINGS.LIST'],
    children: [
      {
        id: 'payments',
        title: 'Payments',
        messageId: 'sidebar.app.paymentsSettings.payments',
        url: '/payments',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.PAYMENT_SETTINGS.LIST'],
      },
      {
        id: 'deductions',
        title: 'Deductions',
        messageId: 'sidebar.app.paymentsSettings.deductions',
        url: '/deductions',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.PAYMENT_SETTINGS.LIST'],
      },
      {
        id: 'payment-rates',
        title: 'Payment Rates',
        messageId: 'sidebar.app.paymentsSettings.paymentRates',
        url: '/payment-rates',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.PAYMENT_SETTINGS.LIST'],
      },
      {
        id: 'vat-codes',
        title: 'VAT Codes',
        messageId: 'sidebar.app.paymentsSettings.vatCodes',
        url: '/vat-codes',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.PAYMENT_SETTINGS.LIST'],
      },
    ],
  },
  {
    id: 'timesheets',
    title: 'Timesheets',
    messageId: 'sidebar.app.timesheets',
    type: 'collapse',
    icon: <Icon src={IconTimesheets} alt="Timesheets" />,
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.TIMESHEETS.LIST'],
    children: [
      {
        id: 'timesheets/all',
        title: 'All Timesheets',
        messageId: 'sidebar.app.timesheets.all',
        url: '/timesheets',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.TIMESHEETS.LIST'],
      },
      {
        id: 'timesheets/pending',
        title: 'Pending Agency Approval',
        messageId: 'sidebar.app.timesheets.pendingAgencyApproval',
        url: '/timesheets/pending',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.TIMESHEETS.LIST'],
      },
      {
        id: 'timesheets/generate-draft-payroll',
        title: 'Generate Draft Payroll',
        messageId: 'sidebar.app.timesheets.generateDraftPayroll',
        url: '/timesheets/generate-draft-payroll',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.TIMESHEETS.LIST'],
      },
      {
        id: 'timesheets/awaiting-invoice-generation',
        title: 'Awaiting Invoice Generation',
        messageId: 'sidebar.app.timesheets.awaitingInvoiceGeneration',
        url: '/timesheets/awaiting-invoice-generation',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.TIMESHEETS.LIST'],
      },
    ],
  },
  {
    id: 'invoices',
    title: 'Invoices',
    messageId: 'sidebar.app.invoices.all',
    icon: <Icon src={IconInvoices} alt="Invoices" />,
    url: '/invoices',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.INVOICES.LIST', 'AGENCY.INVOICES.LIST'],
  },
  {
    id: 'invoices/credit-notes',
    title: 'Credit Notes',
    messageId: 'sidebar.app.invoices.creditNotes',
    icon: <TbReceiptRefund />,
    url: '/invoices/credit-notes',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.CREDIT_NOTES.LIST', 'AGENCY.CREDIT_NOTES.LIST'],
  },
  {
    id: 'timesheets-employee',
    title: 'Timesheets',
    messageId: 'sidebar.app.timesheets',
    icon: <Icon src={IconTimesheets} alt="Timesheets" />,
    url: '/timesheets-employee',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'reports',
    title: 'Reports',
    messageId: 'sidebar.app.reports',
    type: 'collapse',
    icon: <TbReportAnalytics />,
    url: '/reports',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.REPORTS.LIST'],
  },
  {
    id: 'documents',
    title: 'Documents',
    messageId: 'sidebar.app.documents',
    icon: <TbFileText />,
    url: '/documents',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.DOCUMENTS.LIST'],
  },
  {
    id: 'import',
    title: 'Import',
    messageId: 'sidebar.app.import',
    type: 'collapse',
    icon: <TbFileImport />,
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.IMPORT.LIST'],
    children: [
      {
        id: 'import/agencies',
        title: 'Agencies',
        messageId: 'sidebar.app.import.agencies',
        url: '/import/agencies',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.IMPORT.LIST'],
      },
      {
        id: 'import/employees-subcontractors',
        title: 'Employees',
        messageId: 'sidebar.app.import.employeesSubcontractors',
        url: '/import/employees-subcontractors',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.IMPORT.LIST'],
      },
      {
        id: 'import/prospects',
        title: 'Prospects',
        messageId: 'sidebar.app.import.prospects',
        url: '/import/prospects',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.IMPORT.LIST'],
      },
      {
        id: 'import/timesheets',
        title: 'Timesheets',
        messageId: 'sidebar.app.import.timesheets',
        url: '/import/timesheets',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.IMPORT.LIST'],
      },
    ],
  },
  {
    id: 'export',
    title: 'Export',
    messageId: 'sidebar.app.export',
    type: 'collapse',
    icon: <TbFileExport />,
    url: '/export',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.EXPORT.LIST'],
  },
  {
    id: 'KID',
    title: 'KID',
    messageId: 'sidebar.app.KID',
    type: 'collapse',
    icon: <TbId />,
    url: '/KID',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.KID.LIST'],
  },
  {
    id: 'rti-filings-history',
    title: 'RTI Filings History',
    messageId: 'sidebar.app.RTIFilingsHistory',
    type: 'collapse',
    icon: <TbHistory />,
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.RTI_FILINGS.LIST'],
    children: [
      {
        id: 'rti-filings/fps',
        title: 'FPS',
        messageId: 'sidebar.app.RTIFilingsHistory.FPS',
        url: '/rti-filings/fps',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.RTI_FILINGS.LIST'],
      },
      {
        id: 'rti-filings/eps',
        title: 'EPS',
        messageId: 'sidebar.app.RTIFilingsHistory.EPS',
        url: '/rti-filings/eps',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.RTI_FILINGS.LIST'],
      },
    ],
  },
  {
    id: 'employee-documents',
    title: 'Documents',
    messageId: 'sidebar.app.documents',
    icon: <TbFileText />,
    url: '/employee-documents',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'activity-log',
    title: 'Activity Logs',
    messageId: 'sidebar.app.activityLog',
    type: 'collapse',
    icon: <Icon src={IconActivityLogs} alt="Activity Logs" />,
    url: '/activity-log',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.ACTIVITY_LOG.LIST'],
  },
  {
    id: 'permissions',
    title: 'Permissions',
    messageId: 'sidebar.app.permissions',
    type: 'collapse',
    icon: <Icon src={IconPermissions} alt="Permissions" />,
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.PERMISSIONS.LIST'],
    children: [
      {
        id: 'permissions/company',
        title: 'employer',
        messageId: 'sidebar.app.permissions.company',
        url: '/permissions/company',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.PERMISSIONS.LIST'],
      },
      {
        id: 'permissions/employees',
        title: 'Employees',
        messageId: 'sidebar.app.permissions.employees',
        url: '/permissions/employees',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.PERMISSIONS.LIST'],
      },
    ],
  },
];

const bureauIrelandRoutesConfig = [
  {
    id: 'dashboards',
    title: 'Dashboard',
    messageId: 'sidebar.app.dashboard',
    icon: <Icon src={IconDashboard} alt="Dashboard" />,
    url: '/dashboards',
    allowedRoles: [admin, employer, employee],
    permissionNames: ['COMPANY.DASHBOARD.LIST'],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'analytics',
    title: 'Analytics',
    messageId: 'sidebar.app.analytics',
    icon: <Icon src={IconAnalytics} alt="Analytics" />,
    url: '/analytics',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.ANALYTICS.LIST'],
  },
  {
    id: 'companies',
    title: 'Employers',
    messageId: 'common.bureau.companies',
    icon: <Icon src={IconCompanies} alt="Companies" />,
    url: '/companies',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.COMPANIES.LIST'],
  },
  {
    id: 'general',
    title: 'Profile',
    messageId: 'sidebar.app.profile',
    icon: <Icon src={IconEmployee} alt="Profile" />,
    url: '/general',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'payslip',
    title: 'Payslip',
    messageId: 'sidebar.app.payslips',
    icon: <Icon src={IconInvoices} alt="Payslip" />,
    url: '/payslip',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'earning-statement',
    title: 'CIS Statement of Earnings',
    messageId: 'sidebar.app.cisStatementOfEarnings',
    icon: <Icon src={IconInvoices} alt="CIS Statement" />,
    url: '/earning-statement',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'payroll',
    title: 'Payroll',
    messageId: 'sidebar.app.payroll',
    icon: <Icon src={IconInvoices} alt="Payroll" />,
    url: '/payroll',
    allowedRoles: [employer],
  },
  { type: 'divider' } as any,
  {
    id: 'employees-subcontractors',
    title: 'Employees',
    messageId: 'sidebar.app.employeeSubcontractors',
    icon: <Icon src={IconEmployee} alt="Employees" />,
    url: '/employees-subcontractors',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.EMPLOYEES.LIST'],
  },
  {
    id: 'prospects',
    title: 'Prospects',
    messageId: 'sidebar.app.prospects',
    icon: <Icon src={IconProspects} alt="Prospects" />,
    url: '/prospects',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.PROSPECTS.LIST'],
  },
  { type: 'divider' } as { type: string },
  rpnMenuItem,
  aepnMenuItem,
  submitPsrMenuItem,
  {
    id: 'bulk-update',
    title: 'Bulk Update',
    messageId: 'sidebar.app.bulkUpdate',
    icon: <TbDatabaseImport />,
    url: '/bulk-update',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.BULK_UPDATE.LIST'],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'payments-settings',
    title: 'Payments Settings',
    messageId: 'sidebar.app.paymentsSettings',
    type: 'collapse',
    icon: <Icon src={IconPaymentSettings} alt="Payments Settings" />,
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.PAYMENT_SETTINGS.LIST'],
    children: [
      {
        id: 'payments',
        title: 'Payments',
        messageId: 'sidebar.app.paymentsSettings.payments',
        url: '/payments',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.PAYMENT_SETTINGS.LIST'],
      },
      {
        id: 'deductions',
        title: 'Deductions',
        messageId: 'sidebar.app.paymentsSettings.deductions',
        url: '/deductions',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.PAYMENT_SETTINGS.LIST'],
      },
      {
        id: 'payment-rates',
        title: 'Payment Rates',
        messageId: 'sidebar.app.paymentsSettings.paymentRates',
        url: '/payment-rates',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.PAYMENT_SETTINGS.LIST'],
      },
      {
        id: 'vat-codes',
        title: 'VAT Codes',
        messageId: 'sidebar.app.paymentsSettings.vatCodes',
        url: '/vat-codes',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.PAYMENT_SETTINGS.LIST'],
      },
    ],
  },
  {
    id: 'timesheets',
    title: 'Timesheets',
    messageId: 'sidebar.app.timesheets',
    type: 'collapse',
    icon: <Icon src={IconTimesheets} alt="Timesheets" />,
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.TIMESHEETS.LIST'],
    children: [
      {
        id: 'timesheets/all',
        title: 'All Timesheets',
        messageId: 'sidebar.app.timesheets.all',
        url: '/timesheets',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.TIMESHEETS.LIST'],
      },
      {
        id: 'timesheets/pending',
        title: 'Pending Agency Approval',
        messageId: 'sidebar.app.timesheets.pendingAgencyApproval',
        url: '/timesheets/pending',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.TIMESHEETS.LIST'],
      },
      {
        id: 'timesheets/generate-draft-payroll',
        title: 'Generate Draft Payroll',
        messageId: 'sidebar.app.timesheets.generateDraftPayroll',
        url: '/timesheets/generate-draft-payroll',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.TIMESHEETS.LIST'],
      },
    ],
  },
  {
    id: 'invoices',
    title: 'Invoices',
    messageId: 'sidebar.app.invoices.all',
    icon: <Icon src={IconInvoices} alt="Invoices" />,
    url: '/invoices',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.INVOICES.LIST', 'AGENCY.INVOICES.LIST'],
  },
  {
    id: 'invoices/credit-notes',
    title: 'Credit Notes',
    messageId: 'sidebar.app.invoices.creditNotes',
    icon: <TbReceiptRefund />,
    url: '/invoices/credit-notes',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.CREDIT_NOTES.LIST', 'AGENCY.CREDIT_NOTES.LIST'],
  },
  {
    id: 'timesheets-employee',
    title: 'Timesheets',
    messageId: 'sidebar.app.timesheets.timesheetsEmployee',
    icon: <Icon src={IconTimesheets} alt="Timesheets" />,
    url: '/timesheets-employee',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'reports',
    title: 'Reports',
    messageId: 'sidebar.app.reports',
    type: 'collapse',
    icon: <TbReportAnalytics />,
    url: '/reports',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.REPORTS.LIST'],
  },
  {
    id: 'documents',
    title: 'Documents',
    messageId: 'sidebar.app.documents',
    type: 'collapse',
    icon: <TbFileText />,
    url: '/documents',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.DOCUMENTS.LIST'],
  },
  {
    id: 'import',
    title: 'Import',
    messageId: 'sidebar.app.import',
    type: 'collapse',
    icon: <TbFileImport />,
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.IMPORT.LIST'],
    children: [
      {
        id: 'import/agencies',
        title: 'Agencies',
        messageId: 'sidebar.app.import.agencies',
        url: '/import/agencies',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.IMPORT.LIST'],
      },
      {
        id: 'import/employees-subcontractors',
        title: 'Employees',
        messageId: 'sidebar.app.import.employeesSubcontractors',
        url: '/import/employees-subcontractors',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.IMPORT.LIST'],
      },
      {
        id: 'import/prospects',
        title: 'Prospects',
        messageId: 'sidebar.app.import.prospects',
        url: '/import/prospects',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.IMPORT.LIST'],
      },
      {
        id: 'import/timesheets',
        title: 'Timesheets',
        messageId: 'sidebar.app.import.timesheets',
        url: '/import/timesheets',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.IMPORT.LIST'],
      },
    ],
  },
  {
    id: 'export',
    title: 'Export',
    messageId: 'sidebar.app.export',
    type: 'collapse',
    icon: <TbFileExport />,
    url: '/export',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.EXPORT.LIST'],
  },
  // {
  //   id: 'KID',
  //   title: 'KID',
  //   messageId: 'sidebar.app.KID',
  //   type: 'collapse',
  //   icon: <TbId />,
  //   url: '/KID',
  //   allowedRoles: [admin, employer],
  //   permissionNames: ['COMPANY.KID.LIST'],
  // },
  revenueFilingsHistoryMenuItem,
  {
    id: 'employee-documents',
    title: 'Documents',
    messageId: 'sidebar.app.documents',
    icon: <TbFileText />,
    url: '/employee-documents',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'activity-log',
    title: 'Activity Logs',
    messageId: 'sidebar.app.activityLog',
    type: 'collapse',
    icon: <Icon src={IconActivityLogs} alt="Activity Logs" />,
    url: '/activity-log',
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.ACTIVITY_LOG.LIST'],
  },
  {
    id: 'permissions',
    title: 'Permissions',
    messageId: 'sidebar.app.permissions',
    type: 'collapse',
    icon: <Icon src={IconPermissions} alt="Permissions" />,
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.PERMISSIONS.LIST'],
    children: [
      {
        id: 'permissions/company',
        title: 'employer',
        messageId: 'sidebar.app.permissions.company',
        url: '/permissions/company',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.PERMISSIONS.LIST'],
      },
      {
        id: 'permissions/employees',
        title: 'Employees',
        messageId: 'sidebar.app.permissions.employees',
        url: '/permissions/employees',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.PERMISSIONS.LIST'],
      },
    ],
  },
];

const umbrellaRoutesConfig = [
  {
    id: 'dashboards',
    title: 'Dashboard',
    messageId: 'sidebar.app.dashboard',
    icon: <Icon src={IconDashboard} alt="Dashboard" />,
    url: '/dashboards',
    allowedRoles: [admin, agency, employee],
    permissionNames: ['COMPANY.DASHBOARD.LIST', 'AGENCY.DASHBOARD.LIST'],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'analytics',
    title: 'Analytics',
    messageId: 'sidebar.app.analytics',
    icon: <Icon src={IconAnalytics} alt="Analytics" />,
    url: '/analytics',
    allowedRoles: [admin],
    permissionNames: ['COMPANY.ANALYTICS.LIST'],
  },
  {
    id: 'companies',
    title: 'Companies',
    messageId: 'sidebar.app.companies',
    icon: <Icon src={IconCompanies} alt="Companies" />,
    url: '/companies',
    allowedRoles: [admin],
    permissionNames: ['COMPANY.COMPANIES.LIST'],
  },
  {
    id: 'general',
    title: 'Profile',
    messageId: 'sidebar.app.profile',
    icon: <Icon src={IconEmployee} alt="Profile" />,
    url: '/general',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'payslip',
    title: 'Payslip',
    messageId: 'sidebar.app.payslips',
    icon: <Icon src={IconInvoices} alt="Payslip" />,
    url: '/payslip',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'earning-statement',
    title: 'CIS Statement of Earnings',
    messageId: 'sidebar.app.cisStatementOfEarnings',
    icon: <Icon src={IconInvoices} alt="CIS Statement" />,
    url: '/earning-statement',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'employees-subcontractors',
    title: 'Employees',
    messageId: 'sidebar.app.employeeSubcontractors',
    icon: <Icon src={IconEmployee} alt="Employees" />,
    url: '/employees-subcontractors',
    allowedRoles: [admin, agency],
    permissionNames: ['COMPANY.EMPLOYEES.LIST', 'AGENCY.EMPLOYEES.LIST'],
  },
  {
    id: 'agency',
    title: 'agency',
    messageId: 'sidebar.app.agency',
    icon: <Icon src={IconAgency} alt="Agency" />,
    url: '/agency',
    allowedRoles: [admin],
    permissionNames: ['COMPANY.AGENCIES.LIST'],
  },
  {
    id: 'assignments',
    title: 'Assignments',
    messageId: 'sidebar.app.assignments',
    type: 'collapse',
    icon: <TbClipboardList />,
    url: '/assignments',
    allowedRoles: [admin, agency, employee],
    permissionNames: ['COMPANY.ASSIGNMENTS.LIST', 'AGENCY.ASSIGNMENTS.LIST'],
  },
  {
    id: 'prospects',
    title: 'Prospects',
    messageId: 'sidebar.app.prospects',
    icon: <Icon src={IconProspects} alt="Prospects" />,
    url: '/prospects',
    allowedRoles: [admin, agency],
    permissionNames: ['COMPANY.PROSPECTS.LIST', 'AGENCY.PROSPECTS.LIST'],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'hmrc-update',
    title: 'HMRC Update',
    messageId: 'sidebar.app.hmrcUpdate',
    type: 'collapse',
    icon: <TbCloudDownload />,
    allowedRoles: [admin],
    permissionNames: ['COMPANY.HMRC_UPDATES.LIST'],
    children: [
      {
        id: 'hmrc-update/p6-p9',
        title: 'P6/P9 Update',
        messageId: 'sidebar.app.hmrcUpdate.p6p9',
        url: 'hmrc-update/p6-p9',
        allowedRoles: [admin],
        permissionNames: ['COMPANY.HMRC_UPDATES.LIST'],
      },
      {
        id: 'hmrc-update/student-loan',
        title: 'P6/P9 Update',
        messageId: 'sidebar.app.hmrcUpdate.studentLoan',
        url: 'hmrc-update/student-loan',
        allowedRoles: [admin],
        permissionNames: ['COMPANY.HMRC_UPDATES.LIST'],
      },
    ],
  },
  {
    id: 'bulk-update',
    title: 'Bulk Update',
    messageId: 'sidebar.app.bulkUpdate',
    icon: <TbDatabaseImport />,
    url: '/bulk-update',
    allowedRoles: [admin],
    permissionNames: ['COMPANY.BULK_UPDATE.LIST'],
  },
  {
    id: 'statutory-payments',
    title: 'Statutory Payments',
    messageId: 'sidebar.app.statutory',
    type: 'collapse',
    icon: <TbCoins />,
    allowedRoles: [admin, employer],
    permissionNames: ['COMPANY.STATUTORY_PAYMENTS.LIST'],
    children: [
      {
        id: 'statutory-leave',
        title: 'Leave',
        messageId: 'sidebar.app.statutory.leave',
        url: '/statutory-leave',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.STATUTORY_PAYMENTS.LIST'],
      },
      {
        id: 'statutory-payment',
        title: 'Payments',
        messageId: 'sidebar.app.statutory.payments',
        url: '/statutory-payment',
        allowedRoles: [admin, employer],
        permissionNames: ['COMPANY.STATUTORY_PAYMENTS.LIST'],
      },
    ],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'payments-settings',
    title: 'Payments Settings',
    messageId: 'sidebar.app.paymentsSettings',
    type: 'collapse',
    icon: <Icon src={IconPaymentSettings} alt="Payments Settings" />,
    allowedRoles: [admin],
    permissionNames: ['COMPANY.PAYMENT_SETTINGS.LIST'],
    children: [
      {
        id: 'payments',
        title: 'Payments',
        messageId: 'sidebar.app.paymentsSettings.payments',
        url: '/payments',
        allowedRoles: [admin],
        permissionNames: ['COMPANY.PAYMENT_SETTINGS.LIST'],
      },
      {
        id: 'deductions',
        title: 'Deductions',
        messageId: 'sidebar.app.paymentsSettings.deductions',
        url: '/deductions',
        allowedRoles: [admin],
        permissionNames: ['COMPANY.PAYMENT_SETTINGS.LIST'],
      },
      {
        id: 'payment-rates',
        title: 'Payment Rates',
        messageId: 'sidebar.app.paymentsSettings.paymentRates',
        url: '/payment-rates',
        allowedRoles: [admin],
        permissionNames: ['COMPANY.PAYMENT_SETTINGS.LIST'],
      },
      {
        id: 'vat-codes',
        title: 'VAT Codes',
        messageId: 'sidebar.app.paymentsSettings.vatCodes',
        url: '/vat-codes',
        allowedRoles: [admin],
        permissionNames: ['COMPANY.PAYMENT_SETTINGS.LIST'],
      },
    ],
  },
  {
    id: 'timesheets',
    title: 'Timesheets',
    messageId: 'sidebar.app.timesheets',
    type: 'collapse',
    icon: <Icon src={IconTimesheets} alt="Timesheets" />,
    allowedRoles: [admin],
    permissionNames: ['COMPANY.TIMESHEETS.LIST'],
    children: [
      {
        id: 'timesheets/all',
        title: 'All Timesheets',
        messageId: 'sidebar.app.timesheets.all',
        url: '/timesheets',
        allowedRoles: [admin],
        permissionNames: ['COMPANY.TIMESHEETS.LIST'],
      },
      {
        id: 'timesheets/pending',
        title: 'Pending Agency Approval',
        messageId: 'sidebar.app.timesheets.pendingAgencyApproval',
        url: '/timesheets/pending',
        allowedRoles: [admin],
        permissionNames: ['COMPANY.TIMESHEETS.LIST'],
      },
      {
        id: 'timesheets/generate-draft-payroll',
        title: 'Generate Draft Payroll',
        messageId: 'sidebar.app.timesheets.generateDraftPayroll',
        url: '/timesheets/generate-draft-payroll',
        allowedRoles: [admin],
        permissionNames: ['COMPANY.TIMESHEETS.LIST'],
      },
      {
        id: 'timesheets/awaiting-invoice-generation',
        title: 'Awaiting Invoice Generation',
        messageId: 'sidebar.app.timesheets.awaitingInvoiceGeneration',
        url: '/timesheets/awaiting-invoice-generation',
        allowedRoles: [admin],
        permissionNames: ['COMPANY.TIMESHEETS.LIST'],
      },
    ],
  },
  {
    id: 'invoices',
    title: 'Invoices',
    messageId: 'sidebar.app.invoices.all',
    icon: <Icon src={IconInvoices} alt="Invoices" />,
    url: '/invoices',
    allowedRoles: [admin, agency],
    permissionNames: ['COMPANY.INVOICES.LIST', 'AGENCY.INVOICES.LIST'],
  },
  {
    id: 'invoices/credit-notes',
    title: 'Credit Notes',
    messageId: 'sidebar.app.invoices.creditNotes',
    icon: <TbReceiptRefund />,
    url: '/invoices/credit-notes',
    allowedRoles: [admin, agency],
    permissionNames: ['COMPANY.CREDIT_NOTES.LIST', 'AGENCY.CREDIT_NOTES.LIST'],
  },
  {
    id: 'timesheets-agency',
    title: 'Timesheets',
    messageId: 'sidebar.app.timesheets',
    icon: <Icon src={IconTimesheets} alt="Timesheets" />,
    url: '/timesheets-agency',
    allowedRoles: [agency],
    permissionNames: ['AGENCY.TIMESHEETS.LIST'],
  },
  {
    id: 'timesheets-employee',
    title: 'Timesheets',
    messageId: 'sidebar.app.timesheets',
    icon: <Icon src={IconTimesheets} alt="Timesheets" />,
    url: '/timesheets-employee',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'reports',
    title: 'Reports',
    messageId: 'sidebar.app.reports',
    type: 'collapse',
    icon: <TbReportAnalytics />,
    url: '/reports',
    allowedRoles: [admin],
    permissionNames: ['COMPANY.REPORTS.LIST'],
  },
  {
    id: 'documents',
    title: 'Documents',
    messageId: 'sidebar.app.documents',
    icon: <TbFileText />,
    url: '/documents',
    allowedRoles: [admin],
    permissionNames: ['COMPANY.DOCUMENTS.LIST'],
  },
  {
    id: 'import',
    title: 'Import',
    messageId: 'sidebar.app.import',
    type: 'collapse',
    icon: <TbFileImport />,
    allowedRoles: [admin],
    permissionNames: ['COMPANY.IMPORT.LIST'],
    children: [
      {
        id: 'import/agencies',
        title: 'Agencies',
        messageId: 'sidebar.app.import.agencies',
        url: '/import/agencies',
        allowedRoles: [admin],
        permissionNames: ['COMPANY.IMPORT.LIST'],
      },
      {
        id: 'import/employees-subcontractors',
        title: 'Employees',
        messageId: 'sidebar.app.import.employeesSubcontractors',
        url: '/import/employees-subcontractors',
        allowedRoles: [admin],
        permissionNames: ['COMPANY.IMPORT.LIST'],
      },
      {
        id: 'import/prospects',
        title: 'Prospects',
        messageId: 'sidebar.app.import.prospects',
        url: '/import/prospects',
        allowedRoles: [admin],
        permissionNames: ['COMPANY.IMPORT.LIST'],
      },
      {
        id: 'import/timesheets',
        title: 'Timesheets',
        messageId: 'sidebar.app.import.timesheets',
        url: '/import/timesheets',
        allowedRoles: [admin],
        permissionNames: ['COMPANY.IMPORT.LIST'],
      },
    ],
  },
  // {
  //   id: 'export',
  //   title: 'Export',
  //   messageId: 'sidebar.app.export',
  //   type: 'collapse',
  //   icon: <TbFileExport />,
  //   url: '/export',
  //   allowedRoles: [admin, employer],
  //   permissionNames: ['COMPANY.EXPORT.LIST'],
  // },
  {
    id: 'KID',
    title: 'KID',
    messageId: 'sidebar.app.KID',
    type: 'collapse',
    icon: <TbId />,
    url: '/KID',
    allowedRoles: [admin, agency],
    permissionNames: ['COMPANY.KID.LIST', 'AGENCY.KID.LIST'],
  },
  {
    id: 'rti-filings-history',
    title: 'RTI Filings History',
    messageId: 'sidebar.app.RTIFilingsHistory',
    type: 'collapse',
    icon: <TbHistory />,
    allowedRoles: [admin],
    permissionNames: ['COMPANY.RTI_FILINGS.LIST'],
    children: [
      {
        id: 'rti-filings/fps',
        title: 'FPS',
        messageId: 'sidebar.app.RTIFilingsHistory.FPS',
        url: '/rti-filings/fps',
        allowedRoles: [admin],
        permissionNames: ['COMPANY.RTI_FILINGS.LIST'],
      },
      {
        id: 'rti-filings/eps',
        title: 'EPS',
        messageId: 'sidebar.app.RTIFilingsHistory.EPS',
        url: '/rti-filings/eps',
        allowedRoles: [admin],
        permissionNames: ['COMPANY.RTI_FILINGS.LIST'],
      },
      {
        id: 'rti-filings/cis-300',
        title: 'CIS 300',
        messageId: 'sidebar.app.RTIFilingsHistory.CIS300',
        url: '/rti-filings/cis-300',
        allowedRoles: [admin],
        permissionNames: ['COMPANY.RTI_FILINGS.LIST'],
      },
    ],
  },
  {
    id: 'agency-documents',
    title: 'Documents',
    messageId: 'sidebar.app.documents',
    icon: <TbFileText />,
    url: '/agency-documents',
    allowedRoles: [agency],
    permissionNames: ['AGENCY.DOCUMENTS.LIST'],
  },
  {
    id: 'employee-documents',
    title: 'Documents',
    messageId: 'sidebar.app.documents',
    icon: <TbFileText />,
    url: '/employee-documents',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'agency-reports',
    title: 'Reports',
    messageId: 'sidebar.app.reports',
    type: 'collapse',
    icon: <TbReportAnalytics />,
    allowedRoles: [agency],
    permissionNames: ['AGENCY.AWR.LIST', 'AGENCY.WTR.LIST'],
    children: [
      {
        id: 'reports/agency/awr',
        title: 'AWR',
        messageId: 'common.awr',
        url: '/reports/agency/awr',
        allowedRoles: [agency],
        permissionNames: ['AGENCY.AWR.LIST'],
      },
      {
        id: 'reports/agency/wtr',
        title: 'WTR',
        messageId: 'common.wtr',
        url: '/reports/agency/wtr',
        allowedRoles: [agency],
        permissionNames: ['AGENCY.WTR.LIST'],
      },
    ],
  },
  {
    id: 'reports/agency/employment-intermediary',
    title: 'Employment Intermediary',
    messageId: 'common.employmentIntermediary',
    icon: <TbReportAnalytics />,
    url: '/reports/agency/employment-intermediary',
    allowedRoles: [agency],
    permissionNames: ['AGENCY.EMPLOYMENT_INTERMEDIARY_REPORT.LIST'],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'activity-log',
    title: 'Activity Logs',
    messageId: 'sidebar.app.activityLog',
    type: 'collapse',
    icon: <Icon src={IconActivityLogs} alt="Activity Logs" />,
    url: '/activity-log',
    allowedRoles: [admin],
    permissionNames: ['COMPANY.ACTIVITY_LOG.LIST'],
  },
  {
    id: 'permissions',
    title: 'Permissions',
    messageId: 'sidebar.app.permissions',
    type: 'collapse',
    icon: <Icon src={IconPermissions} alt="Permissions" />,
    allowedRoles: [admin],
    permissionNames: ['COMPANY.PERMISSIONS.LIST'],
    children: [
      {
        id: 'permissions/company',
        title: 'employer',
        messageId: 'sidebar.app.permissions.company',
        url: '/permissions/company',
        allowedRoles: [admin],
        permissionNames: ['COMPANY.PERMISSIONS.LIST'],
      },
      {
        id: 'permissions/candidates',
        title: 'Candidates',
        messageId: 'sidebar.app.permissions.candidates',
        url: '/permissions/candidates',
        allowedRoles: [admin],
        permissionNames: ['COMPANY.PERMISSIONS.LIST'],
      },
      {
        id: 'permissions/agency',
        title: 'agency',
        messageId: 'sidebar.app.permissions.agency',
        url: '/permissions/agency',
        allowedRoles: [admin],
        permissionNames: ['COMPANY.PERMISSIONS.LIST'],
      },
    ],
  },
];

/**
 * `agency-reports` keeps running for umbrella-Ireland (48-hour WTR still
 * applies) but its AWR child has no Ireland equivalent (ticket: "No AWR").
 * Derived from the UK item so the WTR entry itself is byte-identical.
 */
const agencyReportsIrelandMenuItem = (() => {
  const ukItem = umbrellaRoutesConfig.find(
    (item: any) => item.id === 'agency-reports',
  ) as any;
  return {
    ...ukItem,
    permissionNames: ['AGENCY.WTR.LIST'],
    children: ukItem.children.filter(
      (child: any) => child.id === 'reports/agency/wtr',
    ),
  };
})();

// The umbrella sidebar has carried Export commented out since the routes were
// made dynamic, while bureau Ireland lists it — so an Irish umbrella tenant had
// no way to reach a page that is registered, permissioned and working
// (`routeModuleMap.export`, admin + employer, COMPANY.EXPORT.LIST). Re-added
// here only, keyed off Import so it lands in the same slot as on bureau
// Ireland; whether UK umbrella should get it back is a separate product call,
// so that config is left exactly as it was.
const exportIrelandMenuItem = {
  id: 'export',
  title: 'Export',
  messageId: 'sidebar.app.export',
  type: 'collapse',
  icon: <TbFileExport />,
  url: '/export',
  allowedRoles: [admin, employer],
  permissionNames: ['COMPANY.EXPORT.LIST'],
};

const umbrellaIrelandRoutesConfig = composeUmbrellaIrelandRoutes(
  umbrellaRoutesConfig as any,
  {
    'hmrc-update': [rpnMenuItem, aepnMenuItem, submitPsrMenuItem],
    'rti-filings-history': [revenueFilingsHistoryMenuItem],
    'agency-reports': [agencyReportsIrelandMenuItem],
    import: [
      umbrellaRoutesConfig.find((item: any) => item.id === 'import') as any,
      exportIrelandMenuItem as any,
    ],
  },
);

const routesConfig = (
  isBureau: boolean,
  payrollCountry: PayrollCountryType,
) => {
  if (payrollCountry === 'IRELAND') {
    return isBureau ? bureauIrelandRoutesConfig : umbrellaIrelandRoutesConfig;
  }
  return isBureau ? bureauRoutesConfig : umbrellaRoutesConfig;
};

export {
  bureauRoutesConfig,
  bureauIrelandRoutesConfig,
  umbrellaRoutesConfig,
  umbrellaIrelandRoutesConfig,
};
export default routesConfig;
